import JSZip from 'jszip';
import { MESSAGE_TYPES } from 'components/notifications';
import { addMessage, clearMessages } from './app';
import { saveAs } from 'file-saver';
import { keyValueMirror } from 'store/store-functions';
import { RPC_TYPES, exists, expandPublishFormats } from 'utils';
import axios from 'axios';
import default3DRpc from 'assets/Ui/default3DModel.png';

// TODO: Move this to constants or somewhere else when refactor is complete.
export const Proxies = {
  billboard: 'Billboard',
  boundingBox: 'Bounding Box',
  simpleMesh: 'Simplified Mesh',
  customMesh: 'Custom Mesh',
};

export const actions = keyValueMirror(
  'GET_USER_CREATOR_JOBS',
  'GET_RPC_CATEGORIES',
  'DOWNLOAD_MODEL',
  'DELETE_MODEL',
  'REPROCESS_MODEL',
  'SET_MODEL_LOADED',
  'SET_HIGH_INSTANCE_MESH_COUNT',
  'SET_ENGINE_STATE',
  'SET_PUBLISH_NAME',
  'CLEAR_PUBLISH_NAME',
  'GET_ALL_SAVE_DATA',
  'GET_MODEL_SAVE_DATA',
  'SET_MODEL_SAVE_DATA',
  'GET_MODEL_METADATA',
  'CLEAR_MODEL_METADATA',
);

const { VITE_API_HOST: apiHost, VITE_API_DEV_HOST } = process.env;
const apiDevHost = VITE_API_DEV_HOST;

const MULTIPLE_FILES_MODEL_EXTENSIONS = [ 'rpc', 'fbx', 'obj', 'dae', 'gltf', 'glb', 'usd', 'usdz', 'ifc' ];

const IMAGE_EXTENSIONS_FOR_AI = [ 'png', 'jpg', 'jpeg' ];

const getFileExtension = file => file?.name?.split('.').pop()?.toLowerCase() ?? '';

const isModelExtension = ext => MULTIPLE_FILES_MODEL_EXTENSIONS.includes(ext);

const isImageExtension = ext => IMAGE_EXTENSIONS_FOR_AI.includes(ext);

const formatObjData = (categories, selectableKeys) => {
  return categories.filter(category => category.type === 'model')
    .map(category => {
      return {
        title: category.name,
        value: category.name,
        selectable: selectableKeys?.includes(category.name),
        children: category.subcategories.map(subcategory => {
          return {
            title: subcategory.name,
            value: subcategory.name,
          };
        }),
      };
    });
};

export const getRpcCategories = token => async dispatch => {
  try {
    const url = `${apiDevHost}/metadata`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Categories not found.');
    }

    const data = await res.json();
    const payload = { payload: formatObjData(data.categories, [ 'People', 'Other' ]) };
    const action = { type: actions.GET_RPC_CATEGORIES, ...payload };

    dispatch(action);
  } catch (e) {
    const message = e.message || 'Error retrieving categories';
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  }
};

export const downloadModel = args => async dispatch => {
  const { rpcGuid, format, onComplete, onError, token, download, saveEnabled, showModal } = args;

  try {
    const onModelsPage = window.location.pathname.includes('/models/');
    const preserveOriginalData = true;

    const messageConfig = currentStep => ({
      type: MESSAGE_TYPES.loading,
      showModal: exists(showModal) ? showModal : true,
      currentStep,
      totalSteps: saveEnabled && onModelsPage ? 4 : 2,
      closeButton: false,
    });

    const url = `${apiDevHost}/models/${rpcGuid}/download/${format}`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (token) {
      options.headers.Authorization = `Bearer ${token}`;
    }

    if (onModelsPage && saveEnabled) {
      dispatch(
        addMessage([ {
          message: 'Checking data...',
          config: messageConfig(1),
        } ]),
      );

      await dispatch(getModelSaveData(token, rpcGuid, preserveOriginalData));
      dispatch(clearMessages());
    }

    if (!download) {
      dispatch(
        addMessage([ {
          message: 'Linking to model...',
          config: messageConfig(saveEnabled && onModelsPage ? 2 : 1),
        } ]),
      );
    }

    const res = await fetch(url, options);

   if (res.status === 404) {
      dispatch(clearMessages());

      if (onError) {
        onError?.();
      } else {
        setTimeout(() => {
          window.location.href = window.location.origin;
        }, 4000);

        dispatch(
          addMessage([ {
            message: 'Unable to find model. Redirecting...',
            config: {
              type: MESSAGE_TYPES.error,
              showModal: true,
              closeButton: false,
            },
          } ]),
        );
      }

      return;
    }

    if (!res.ok) {
      dispatch(clearMessages());
      dispatch(
        addMessage([ {
          message: 'Unable to load model.',
          config: { type: MESSAGE_TYPES.error },
        } ]),
      );

      return;
    }

    const data = await res.json();

    if (download) {
      return window.location.href = data.url;
    }

    return data.url;
  } catch (e) {
    const message = e?.message || 'Error downloading model';
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };

    dispatch(clearMessages());
    dispatch(addMessage([ { message, config } ]));
  } finally {
    onComplete?.();
  }
};

export const deleteModel = (job, token) => async dispatch => {
  try {
    const complete = job.status === 'COMPLETED';
    const id = complete ? job.rpc_guid : job.job_guid;
    const url = complete ? `${apiDevHost}/models/${id}` : `${apiDevHost}/jobs/${id}`;
    const options = {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Job ID not found.');
    }

    const data = await res.json();
    const action = { type: actions.DELETE_MODEL, payload: data };

    dispatch(action);

    if (complete) {
      dispatch(deleteSaveData(token, job.rpc_guid));
    }

    dispatch(
      addMessage([ {
        message: `${job.title || 'selected model'} was deleted`,
        config: { type: MESSAGE_TYPES.deletion, timer: 4 },
      } ]),
    );
  } catch (e) {
    const message = e?.message || 'Error deleting model';
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  }
};

export const reprocessModel = args => async dispatch => {
  try {
    const { job, token, handleSockets } = args;
    const id = job.job_guid;
    const url = `${apiDevHost}/jobs/${id}`;
    const options = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);
    if (res.status === 404) {
      throw new Error('Job Id not found');
    }

    const data = await res.json();
    const action = { type: actions.REPROCESS_MODEL, payload: data };
    dispatch(action);
    handleSockets?.();

    dispatch(
      addMessage([ {
        message: `${job.title || 'selected model'} was sent for reprocessing.`,
        config: { type: MESSAGE_TYPES.success, timer: 4 },
      } ]),
    );
  } catch (e) {
    const message = e.message;
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  }
};


// Polls a single model for its format availability. Deliberately does not dispatch or raise a
// message: it runs on an interval, and GET_MODEL_METADATA feeds the canvas screens.
export const getModelFormats = args => async () => {
  const { rpcGuid, token } = args;

  try {
    const url = `${apiDevHost}/models/${rpcGuid}`;
    const options = { method: 'GET', headers: {} };

    if (token) {
      options.headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(url, options);

    if (!res.ok) return null;

    const data = await res.json();
    const formats = {};

    data?.formats?.forEach(format => formats[format.key] = format.availability);

    return formats;
  } catch {
    return null;
  }
};

export const processModelByFormat = args => async dispatch => {
  const { rpcGuid, format, token, onComplete, onError } = args;

  try {
    const url = `${apiDevHost}/models/${rpcGuid}`;

    const options = {
      method: 'POST',
      body: JSON.stringify({ job_type: format }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    dispatch(
      addMessage([
        {
          message: 'Format was sent for processing.',
          config: { type: MESSAGE_TYPES.success, timer: 2.5 },
        },
      ]),
    );

    const res = await fetch(url, options);

    if (!res.ok) {
      if (res.status === 404) {
        throw new Error('Model Id not found');
      }
      throw new Error(`Request failed with status: ${res.status}`);
    }

    onComplete?.();
  } catch (e) {
    console.error('Error:', e);
    onError?.(e);
    dispatch(
      addMessage([
        { message: e.message, config: { type: MESSAGE_TYPES.error, timer: 2.5 } },
      ]),
    );
  }
};


function buildZipProperties(payload) {
  const properties = {
    proxyMesh: payload.proxyName,
  };

  if (payload.proxyName === Proxies?.simpleMesh) {
    properties.decimateAmount = payload?.decimateAmount ?? 0.5;
  }
  if (payload?.useTextureSimplification ?? false) {
    properties.useTextureSimplification = true;
    properties.textureWidth = payload?.downscaleSize ?? 1024;
    properties.textureHeight = payload?.downscaleSize ?? 1024;
  }
  if (payload?.useModelOptimization ?? false) {
    properties.useModelOptimization = true;
    properties.optimizationAmount = payload?.optimizationAmount ?? 0.5;
  }
  if (payload?.tencentSettings) {
    Object.assign(properties, payload.tencentSettings);
  }
  if (payload?.meshySettings) {
    Object.assign(properties, payload.meshySettings);
  }
  if (payload?.tripoSettings) {
    Object.assign(properties, payload.tripoSettings);
  }
  if (payload?.rodinSettings) {
    Object.assign(properties, payload.rodinSettings);
  }

  return properties;
}

async function addPreviewImageToZip(zip) {
  const response = await fetch(default3DRpc);
  const blob = await response.blob();
  zip.file('preview.png', blob);
}

async function processZipArchive(zip, payloadFile) {
  const archive = await JSZip.loadAsync(payloadFile);
  let job_type = null;
  const extractPromises = [];

  archive.forEach((relativePath, entry) => {
    const ext = getFileExtension({ name: entry.name });

    if (isModelExtension(ext)) {
      if (job_type) {
        throw new Error('Multiple model files provided');
      }
      job_type = RPC_TYPES?.[ext];
      extractPromises.push(
        entry.async('blob').then(blob => {
          zip.file(`model.${ext}`, blob);
        }),
      );
      return;
    }

    const baseName = entry.name.split('/').pop();
    extractPromises.push(
      entry.async('blob').then(blob => {
        zip.file(baseName, blob);
      }),
    );
  });

  await Promise.all(extractPromises);
  return job_type;
}

const VIEW_NAMES = [ 'model', 'left', 'back', 'right' ];

function addImageFilesToZip(zip, imageFiles) {
  imageFiles.forEach((file, index) => {
    const ext = getFileExtension(file) || 'png';
    const baseName = VIEW_NAMES[index] || `image_${index}`;
    zip.file(`${baseName}.${ext}`, file);
  });
}

function addSingleFileToZip(zip, file, payload, jobTypeRef) {
  const ext = getFileExtension(file);
  const isModel = isModelExtension(ext);
  const isImage = ext === 'png' || isImageExtension(ext);

  if (!isModel && !isImage) {
    return;
  }

  if (jobTypeRef.current) {
    throw new Error('Multiple model files provided');
  }

  jobTypeRef.current = payload.ai
    ? RPC_TYPES[payload.aiModel?.toLowerCase()]
    : RPC_TYPES?.[ext];

  zip.file(`model.${ext}`, file);
}

function addMultipleFilesToZip(zip, files, payload, jobTypeRef) {
  const fileList = Array.from(files ?? []);

  if (payload.ai) {
    const imageFiles = fileList.filter(file => isImageExtension(getFileExtension(file)));
    if (imageFiles.length === 0) {
      throw new Error('No image files provided for AI model.');
    }
    jobTypeRef.current = RPC_TYPES[payload.aiModel?.toLowerCase()];
    addImageFilesToZip(zip, imageFiles);
    return;
  }

  fileList.forEach(file => {
    const ext = getFileExtension(file);

    if (isModelExtension(ext)) {
      if (jobTypeRef.current) {
        throw new Error('Multiple model files provided');
      }
      jobTypeRef.current = RPC_TYPES?.[ext];
      zip.file(`model.${ext}`, file);
      return;
    }

    zip.file(file.name, file);
  });
}

export const preparePayloadAndJobType = async payload => {
  const zip = new JSZip();
  const jobTypeRef = { current: null };

  const metadata = { ...payload };

  if (Array.isArray(payload.formats)) {
    metadata.formats = expandPublishFormats(payload.formats);
  }

  zip.file('metadata.json', JSON.stringify(metadata));
  zip.file('properties.json', JSON.stringify(buildZipProperties(payload), null, 2));
  await addPreviewImageToZip(zip);

  const files = payload?.files ?? [];
  const fileCount = files.length;

  if (fileCount === 0) {
    return { job_type: jobTypeRef.current, zip };
  }

  if (fileCount === 1) {
    const file = files[0];
    const ext = getFileExtension(file);

    if (ext === 'zip') {
      jobTypeRef.current = await processZipArchive(zip, file);
      return { job_type: jobTypeRef.current, zip };
    }

    addSingleFileToZip(zip, file, payload, jobTypeRef);
    return { job_type: jobTypeRef.current, zip };
  }

  addMultipleFilesToZip(zip, files, payload, jobTypeRef);
  return { job_type: jobTypeRef.current, zip };
};

export const createRpc = args => async dispatch => {
  const { token, userId, payload, debugState, onComplete, onSuccess, handleSockets, userEmail } = args;

  try {
    let { job_type, zip } = payload;
    const { blob } = payload;

    if (!job_type || (!zip && !blob)) {
      const storeBasedPayload = await preparePayloadAndJobType(payload);
      job_type = storeBasedPayload.job_type;
      zip = storeBasedPayload.zip;
    }

    if (!job_type) {
      throw Error('Model file wasn`t provided');
    }

    const url = `${apiDevHost}/models`;
    const options = {
      method: 'POST',
      body: JSON.stringify({
        jobType: job_type,
        userId,
        requestingApplication: 'FOVEA',
        notificationEmail: userEmail,
      }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    dispatch(clearMessages());
    dispatch(
      addMessage([ {
        message: 'Creating job…',
        config: { type: MESSAGE_TYPES.loading, showModal: true },
      } ]),
    );

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Jobs not found.');
    }

    const data = await res.json();
    const file = blob || await zip.generateAsync({ type: 'blob' });

    const onProgressEvent = e => {
      const loaded = e.loaded;
      const total = e.total || loaded;

      const progress = (bytes, totalBytes) => {
        const conversionUnit = 1024;
        const inKB = bytes / conversionUnit;
        const inMB = inKB / conversionUnit;
        const inGB = inMB / conversionUnit;
        const isGB = inMB >= conversionUnit;
        const unit = isGB ? inGB : inMB;
        const label = isGB ? 'GB' : 'MB';
        const total = parseFloat(totalBytes || 0);
        const stringOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
        const percent = `${(Math.floor((bytes / total) * 100))}%`;
        const log = `${unit?.toLocaleString('en-US', stringOptions)} ${label}`;

        return {
          size: log,
          percent,
        };
      };

      dispatch(clearMessages());
      dispatch(
        addMessage([ {
          message: `Uploading model... ${progress(loaded, total).percent}`,
          config: {
            type: MESSAGE_TYPES.loading,
            showModal: true,
            closeButton: false,
            bytesLoaded: loaded,
            bytesTotal: total,
          },
        } ]),
      );
    };

    const fileOptions = {
      onUploadProgress: e => onProgressEvent(e),
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    };

    await axios.put(data.putUrl, file, fileOptions);

    dispatch(clearMessages());
    dispatch(
      addMessage([ {
        message: 'RPC published.',
        config: {
          type: MESSAGE_TYPES.success,
          timer: 2.5,
          showModal: true,
        },
      } ]),
    );

    if (debugState) {
      saveAs(file, 'RPC.zip');
    }

    handleSockets?.();
    onSuccess?.();
  } catch (e) {
    const message = e.message;
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };

    dispatch(clearMessages());
    dispatch(addMessage([ { message, config } ]));
  } finally {
    onComplete?.();
  }
};

export const setModelLoaded = payload => dispatch => {
  const action = { type: actions.SET_MODEL_LOADED, payload };
  dispatch(action);
};

export const setEngineState = payload => dispatch => {
  const action = { type: actions.SET_ENGINE_STATE, payload };
  dispatch(action);
};

export const setPublishName = publishName => ({
  type: actions.SET_PUBLISH_NAME,
  payload: publishName,
});

export const clearPublishName = () => ({
  type: actions.CLEAR_PUBLISH_NAME,
  payload: '',
});

export const getAllSaveData = token => async dispatch => {
  try {
    const url = `${apiHost}/creator/v1/integration/user/saves`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.message || 'Unable to fetch saved data.');
    }

    const action = { type: actions.GET_ALL_SAVE_DATA, payload: data.data };

    dispatch(action);
  } catch (e) {
    const message = e.message;
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  }
};

export const getModelSaveData = (token, guid, preserveOriginalData) => async dispatch => {
  try {
    const url = `${apiHost}/creator/v1/integration/rpc/${guid}/save`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.message || 'Unable to fetch saved data.');
    }

    const action = {
      type: actions.GET_MODEL_SAVE_DATA,
      payload: data.data,
      meta: { preserveOriginalData },
    };

    dispatch(action);
  } catch (e) {
    const message = e.message;
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  }
};

export const setModelSaveData = args => async dispatch => {
  const {
    token,
    guid,
    payload,
    skipServerUpdate,
    updateOriginalData,
    onComplete,
  } = args;

  try {
    let action = {
      type: actions.SET_MODEL_SAVE_DATA,
      payload,
      meta: { updateOriginalData },
    };

    if (skipServerUpdate) {
      return dispatch(action);
    }

    const url = `${apiHost}/creator/v1/integration/rpc/${guid}/save`;
    const options = {
      method: 'POST',
      body: JSON.stringify({ serialized_data: payload }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.message || 'Unable to save changes.');
    }

    action = { ...action, payload };

    dispatch(action);
    dispatch(
      addMessage([ {
        message: 'Changes saved.',
        config: { type: MESSAGE_TYPES.success, timer: 2.5 },
      } ]),
    );

    onComplete?.();
  } catch (e) {
    const message = e.message;
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  }
};

export const deleteSaveData = (token, guid) => async dispatch => {
  try {
    const url = `${apiHost}/creator/v1/integration/rpc/${guid}/save`;
    const options = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.message || 'Unable to delete save changes.');
    }

  } catch (e) {
    const message = e.message;
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  }
};

export const setHighInstanceMeshCount = payload => ({
  type: actions.SET_HIGH_INSTANCE_MESH_COUNT,
  payload,
});

export const getModelMetadata = guid => async dispatch => {
  try {
    const url = `${apiDevHost}/models/${guid}`;
    const options = { method: 'GET' };

    const res = await fetch(url, options);
    const data = await res.json();

    if (!res.ok) {
      const message = 'Unable to retrieve model metadata.';
      const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
      dispatch(addMessage([ { message, config } ]));
      return;
    }

    dispatch({ type: actions.GET_MODEL_METADATA, payload: data });
  } catch (err) {
    const message = 'Unable to retrieve model metadata.';
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  }
};

export const clearModelMetadata = () => ({
  type: actions.CLEAR_MODEL_METADATA,
});
